import { useState } from "react"
import { motion, useReducedMotion } from "motion/react"
import { Check, Image01 } from "@untitledui/icons"
import type { PublicShopProfile } from "@/lib/api/public-consumer"
import { ViewfinderTarget } from "@/components/onboarding-activation/ui"
import type { DamageReadout } from "./summarize"
import { DamagePanel, DetectionBracket } from "./damage-summary"
import { FlowHeading } from "./flow-chrome"
import { CONSUMER_V2 } from "./copy"

// THE fancy moment — the only deep-pine screen in the whole flow. The stage
// paints #0B2E22 full-bleed, the viewfinder locks onto the personalized
// headline, the consumer's own photo gets the detection bracket, and a white
// rounded-t-3xl panel slides up with the read. Price-free by construction
// (summarize.ts strips every cost field).

const PINE = "#0B2E22"

export function RevealStage({
  shop,
  first,
  readout,
  heroPhotoUrl,
  heroSideLabel,
  onContinue,
  onAddPhoto,
}: {
  shop: PublicShopProfile
  first: string | null
  readout: DamageReadout
  heroPhotoUrl: string | null
  // Fallback tile label when the hero can't render (mock URLs, revoked blobs).
  heroSideLabel: string
  onContinue: () => void
  onAddPhoto: () => void
}) {
  const reduceMotion = useReducedMotion()
  const [heroBroken, setHeroBroken] = useState(false)
  const headline = CONSUMER_V2.reveal.headline(first)
  const primaryLabel = readout.areas[0]?.label ?? "Damage"
  const showHero = heroPhotoUrl && !heroBroken

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="flex min-h-[100dvh] flex-col"
      style={{ backgroundColor: PINE }}
    >
      <div className="mx-auto flex w-full max-w-[560px] flex-1 flex-col px-4 pt-[max(1.5rem,env(safe-area-inset-top))] md:px-6">
        {/* Analysis complete chip */}
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.3 }}
          className="inline-flex w-fit items-center gap-1.5 rounded-full bg-emerald-400/15 px-3 py-1.5 text-[13px] font-semibold text-emerald-300"
        >
          <span className="grid size-3.5 place-items-center rounded-full bg-emerald-400">
            <Check className="size-2.5 text-[#0B2E22]" strokeWidth={4} aria-hidden />
          </span>
          {CONSUMER_V2.reveal.chip}
        </motion.div>

        {/* The viewfinder locks onto the personalized headline. */}
        <div className="mt-3">
          {reduceMotion ? (
            <FlowHeading className="text-[26px] font-bold leading-[1.15] tracking-tight text-white md:text-[30px]">
              {headline}
            </FlowHeading>
          ) : (
            <ViewfinderTarget>
              <FlowHeading className="text-[26px] font-bold leading-[1.15] tracking-tight text-white md:text-[30px]">
                {headline}
              </FlowHeading>
            </ViewfinderTarget>
          )}
        </div>

        {/* HERO — the consumer's own photo with the bracket locking on. Mock
            image URLs don't resolve → side-labelled placeholder tile. */}
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="relative mt-4 overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/40"
        >
          {showHero ? (
            <img
              src={heroPhotoUrl}
              alt="Your vehicle"
              onError={() => setHeroBroken(true)}
              className="max-h-[300px] min-h-[200px] w-full object-cover"
            />
          ) : (
            <div className="grid min-h-[200px] w-full place-items-center bg-white/[0.06]">
              <div className="flex flex-col items-center gap-2 text-emerald-100/60">
                <Image01 className="size-8" aria-hidden />
                <p className="text-[13px] font-semibold">{heroSideLabel}</p>
              </div>
            </div>
          )}
          <div className="pointer-events-none absolute inset-0 bg-black/10" />
          {!reduceMotion && <DetectionBracket label={primaryLabel} />}
          {reduceMotion && (
            <span className="absolute bottom-3 left-3 rounded-full bg-emerald-400 px-2.5 py-1 text-[13px] font-semibold text-pine">
              {primaryLabel}
            </span>
          )}
        </motion.div>

        <div className="flex-1" />
      </div>

      {/* White panel slides up with the read — the payoff. */}
      <motion.div
        initial={reduceMotion ? false : { y: 96, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.35, type: "spring", stiffness: 260, damping: 30 }}
        className="mt-5 w-full rounded-t-3xl bg-white shadow-[0_-16px_48px_rgba(0,0,0,0.35)]"
      >
        <div className="mx-auto w-full max-w-[560px] px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-5 md:px-6">
          <div aria-hidden className="mx-auto mb-4 h-1 w-10 rounded-full bg-zinc-200" />
          <DamagePanel
            readout={readout}
            shop={shop}
            onPrimary={onContinue}
            onAddPhoto={onAddPhoto}
          />
        </div>
      </motion.div>
    </motion.div>
  )
}
